// ─────────────────────────────────────────────────────────────────────
// REPORTING — the per-centre settlement basis.
//
// The app has no transaction table: money lives on three heterogeneous
// records (a booking's priceNok/paymentStatus/payment, an activity
// registration's payment/paidStatus, a seriespill pair's season fee). This
// module flattens them into ONE ledger of receipt-like rows, buckets the rows
// per month + centre, and computes the settlement (gross, platform fee as a
// percentage per transaction plus a fixed monthly fee, net payout).
//
// Pure functions only — no React, no Storage. The view renders what this
// returns; tests exercise the rules directly.
// ─────────────────────────────────────────────────────────────────────

import { courtDisplayName } from './booking';
import { roundOre } from '../lib/money';

export type ReportTxKind = 'booking' | 'activity' | 'seriespill';
export type ReportTxStatus = 'paid' | 'pending' | 'free' | 'refunded';
export type ReportTxMethod = 'vipps' | 'card' | 'manual' | 'booker_cover' | 'none';

/** One line on the settlement basis — a booking, a sign-up, or a season fee. */
export interface ReportTransaction {
  /** Stable id: `${kind}:${sourceId}[:${registrationKey}]`. */
  id: string;
  kind: ReportTxKind;
  status: ReportTxStatus;
  /** Service date (local YYYY-MM-DD) — the month the row settles in. */
  date: string;
  /** Service start time (HH:MM) when known. */
  time: string | null;
  /** When the money moved (epoch ms), when known. */
  paidAtMs: number | null;
  /** What was bought — «Bane 3 · 18:00–19:30 (90 min)», the activity name … */
  title: string;
  /** Secondary line — class, court, season … */
  detail: string;
  /** Who paid — booker name, player name, pair. */
  payer: string;
  method: ReportTxMethod;
  /** Payment-terminal reference (transactionId) when a payment object exists. */
  reference: string | null;
  /** Gross amount in NOK (never negative — refunds keep the amount, status says «refunded»). */
  amountNok: number;
  /** The centre the row belongs to (the club, or a standalone activity's venue). */
  centre: string;
  /** Rows generated by the booking demo-data helper (`notes: '(demo)'`). */
  demo: boolean;
}

export interface ReportingFees {
  /** Platform fee as a percentage of every PAID transaction (e.g. 3 → 3 %). */
  feePct: number;
  /** Fixed platform fee per centre per month, NOK. */
  monthlyFeeNok: number;
}

export const DEFAULT_REPORTING_FEES: ReportingFees = { feePct: 3, monthlyFeeNok: 990 };

export interface ReportingSettings extends ReportingFees {
  /** Include rows from the booking demo-data generator (off by default). */
  includeDemo: boolean;
}

export const DEFAULT_REPORTING_SETTINGS: ReportingSettings = {
  ...DEFAULT_REPORTING_FEES,
  includeDemo: false,
};

export const REPORTING_SETTINGS_KEY = 'reporting_settings_v1';

// ─── local-date helpers (never toISOString — HANDOVER §13) ───

/** Local YYYY-MM-DD for an epoch ms. */
export function localDateOf(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `YYYY-MM` of a local date string (or `''` for garbage). */
export function monthKeyOf(date: string | null | undefined): string {
  return typeof date === 'string' && /^\d{4}-\d{2}/.test(date) ? date.slice(0, 7) : '';
}

/** `YYYY-MM` for the month a Date falls in (local). */
export function monthKeyOfDate(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Shift a `YYYY-MM` key by `delta` months. */
export function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1 + delta, 1);
  return monthKeyOfDate(d);
}

export const MONTH_NAMES_NB = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
];

/** «September 2026» for `2026-09`. */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const name = MONTH_NAMES_NB[(m || 1) - 1] || '';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${y || ''}`.trim();
}

/** «9. sep. 2026» for a local date string. */
export function shortDateLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  const short = [
    'jan',
    'feb',
    'mar',
    'apr',
    'mai',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'des',
  ];
  return `${d}. ${short[m - 1]} ${y}`;
}

/** «Tirsdag 9. september» for a local date string. */
export function longDateLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  const dt = new Date(y, m - 1, d);
  const wd = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'][dt.getDay()];
  return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d}. ${MONTH_NAMES_NB[m - 1]}`;
}

// ─── naming helpers ───

const trim = (s: unknown) => (typeof s === 'string' ? s.trim() : '');

/** The centre an activity belongs to — mirrors memberData.activityVenue. */
export function activityCentreOf(a: any, clubInfo: any): string {
  const standalone = a?.bookingDraft?.slotMode === 'standalone' && trim(a?.bookingDraft?.venueName);
  return standalone || homeCentreOf(clubInfo);
}

/** The club's own centre name. */
export function homeCentreOf(clubInfo: any): string {
  return trim(clubInfo?.name) || 'Padelsenteret';
}

const playerNameOf = (players: any[], id: any): string => {
  if (id == null) return '';
  const p = (players || []).find((x) => String(x?.id) === String(id));
  return trim(p?.name) || trim(p?.displayName) || '';
};

const methodOfPayment = (payment: any, status: ReportTxStatus): ReportTxMethod => {
  const m = trim(payment?.method).toLowerCase();
  if (m === 'vipps' || m === 'card' || m === 'booker_cover') return m as ReportTxMethod;
  if (status === 'paid') return 'manual';
  return 'none';
};

const toPaidMs = (v: any): number | null => (typeof v === 'number' && v > 0 ? v : null);

// ─── ledger: bookings ───

function bookingRows(bookings: any[], clubInfo: any, bookingConfig: any): ReportTransaction[] {
  const centre = homeCentreOf(clubInfo);
  const courtMeta = bookingConfig?.courtMeta || {};
  return (bookings || [])
    .filter((b) => b && b.date && b.status !== 'cancelled')
    .map((b): ReportTransaction => {
      const raw = trim(b.paymentStatus) || 'free';
      const price = Math.max(0, Number(b.priceNok) || 0);
      let status: ReportTxStatus = raw === 'paid' ? 'paid' : raw === 'pending' ? 'pending' : 'free';
      if (status !== 'paid' && price === 0) status = 'free';
      const payment = b.payment || null;
      const dur = Number(b.durationMin) || 0;
      const span = b.endTime ? `${b.startTime}–${b.endTime}` : b.startTime || '';
      const court = courtDisplayName(b.court, courtMeta);
      const title = `${court} · ${span}${dur ? ` (${dur} min)` : ''}`;
      const source = trim(b.source);
      const detail =
        trim(b.title) ||
        (source === 'tournament'
          ? 'Turnering'
          : source === 'activity_linked'
            ? 'Aktivitet'
            : source === 'admin'
              ? 'Registrert av senteret'
              : 'Banebooking');
      return {
        id: `booking:${b.id}`,
        kind: 'booking',
        status,
        date: b.date,
        time: b.startTime || null,
        paidAtMs: toPaidMs(payment?.paidAt) ?? (status === 'paid' ? toPaidMs(b.createdAt) : null),
        title,
        detail,
        payer: trim(b.bookedBy?.name) || trim(b.bookedBy?.email) || 'Ukjent',
        method: methodOfPayment(payment, status),
        reference: trim(payment?.transactionId) || null,
        amountNok: status === 'paid' && payment?.amount != null ? Number(payment.amount) : price,
        centre,
        demo: trim(b.notes) === '(demo)',
      };
    });
}

// ─── ledger: activity registrations ───

/** What a registration owes, mirroring ActivitiesView's pricing rules. */
export function registrationAmount(activity: any, reg: any): number {
  if (reg?.payment?.amount != null && Number.isFinite(Number(reg.payment.amount)))
    return Math.max(0, Number(reg.payment.amount));
  const cfg = activity?.config || {};
  const buckets: any[] = Array.isArray(cfg.kmBuckets) ? cfg.kmBuckets : [];
  if (reg?.bucketKey && buckets.length) {
    const b = buckets.find((x) => x?.bucketKey === reg.bucketKey);
    if (b?.priceNok != null) return Math.max(0, Number(b.priceNok) || 0);
  }
  const perPlayer = Math.max(0, Number(cfg.pricePerPlayer) || 0);
  if (!reg?.isTeam) return perPlayer;
  const custom = ['coaching', 'arrangement', 'custom'].includes(String(cfg.tournamentType || ''));
  const perTeamPriced = custom && cfg.teamSignupMode === 'pairs';
  return perTeamPriced ? perPlayer : perPlayer * 2;
}

function activityRows(activities: any[], players: any[], clubInfo: any): ReportTransaction[] {
  const rows: ReportTransaction[] = [];
  (activities || []).forEach((a) => {
    if (!a) return;
    const cfg = a.config || {};
    const date: string = cfg.activityDate || a.bookingDraft?.date || '';
    if (!date) return;
    const centre = activityCentreOf(a, clubInfo);
    const name = trim(cfg.name) || 'Aktivitet';
    const buckets: any[] = Array.isArray(cfg.kmBuckets) ? cfg.kmBuckets : [];
    (a.registrations || []).forEach((r: any, idx: number) => {
      if (!r) return;
      const raw = trim(r.paidStatus) || 'free';
      const amount = registrationAmount(a, r);
      let status: ReportTxStatus =
        raw === 'paid'
          ? 'paid'
          : raw === 'refunded'
            ? 'refunded'
            : raw === 'pending'
              ? 'pending'
              : 'free';
      if (status === 'pending' && amount === 0) status = 'free';
      const payment = r.payment || null;
      const p1 = playerNameOf(players, r.playerId) || 'Ukjent spiller';
      const p2 = r.partnerId ? playerNameOf(players, r.partnerId) : '';
      const bucket = r.bucketKey ? buckets.find((b) => b?.bucketKey === r.bucketKey) : null;
      const parts: string[] = [];
      if (bucket?.label || bucket?.name) parts.push(trim(bucket.label || bucket.name));
      if (r.isTeam) parts.push('lagpåmelding');
      if (r.status === 'waitlist') parts.push('venteliste');
      rows.push({
        id: `activity:${a.id}:${r.playerId ?? idx}${r.partnerId ? `+${r.partnerId}` : ''}`,
        kind: 'activity',
        status,
        date,
        time: cfg.activityTime || a.bookingDraft?.startTime || null,
        paidAtMs: toPaidMs(payment?.paidAt) ?? (status === 'paid' ? toPaidMs(r.signedUpAt) : null),
        title: name,
        detail: parts.join(' · ') || 'Påmelding',
        payer: p2 ? `${p1} / ${p2}` : p1,
        method: methodOfPayment(payment, status),
        reference: trim(payment?.transactionId) || null,
        amountNok: amount,
        centre,
        demo: false,
      });
    });
  });
  return rows;
}

// ─── ledger: seriespill season fees ───

function seriespillRows(tournaments: any[], clubInfo: any): ReportTransaction[] {
  const centre = homeCentreOf(clubInfo);
  const rows: ReportTransaction[] = [];
  const seen = new Set<string>();
  (tournaments || []).forEach((t) => {
    if (!t || t.type !== 'seriespill') return;
    if (seen.has(String(t.id))) return;
    seen.add(String(t.id));
    const classes: any[] = t.config?.classes || [];
    const pairs: any[] = t.pairs || [];
    const regs = t.seasonRegistrations || {};
    Object.keys(regs).forEach((pairId) => {
      const reg = regs[pairId];
      if (!reg || reg.paidForSeason == null) return;
      const pair = pairs.find((p) => String(p?.id) === String(pairId));
      const cls = pair ? classes.find((c) => c?.id === pair.classId) : null;
      const amount =
        reg.amount != null && Number.isFinite(Number(reg.amount))
          ? Math.max(0, Number(reg.amount))
          : Math.max(0, Number(cls?.seasonFee) || 0);
      const paidAt = toPaidMs(reg.paidAt);
      if (!paidAt) return; // no timestamp → cannot settle it in any month
      rows.push({
        id: `seriespill:${t.id}:${pairId}:${reg.paidForSeason}`,
        kind: 'seriespill',
        status: amount > 0 ? 'paid' : 'free',
        date: localDateOf(paidAt),
        time: null,
        paidAtMs: paidAt,
        title: `Sesongavgift · ${trim(t.name) || 'Seriespill'}`,
        detail: `${trim(cls?.name) || 'Klasse'} · sesong ${reg.paidForSeason}`,
        payer: pair ? `${trim(pair.p1Name) || '?'} / ${trim(pair.p2Name) || '?'}` : 'Ukjent par',
        method: trim(reg.paidBy) && trim(reg.paidBy) !== 'admin' ? 'vipps' : 'manual',
        reference: null,
        amountNok: amount,
        centre,
        demo: false,
      });
    });
  });
  return rows;
}

// ─── the ledger ───

export interface LedgerSources {
  bookings?: any[];
  activities?: any[];
  /** Tournaments incl. live ones (archiveWithLive) — seriespill season fees live here. */
  tournaments?: any[];
  players?: any[];
  clubInfo?: any;
  bookingConfig?: any;
}

/** Every money-relevant row across the app, newest first. */
export function buildLedger(src: LedgerSources): ReportTransaction[] {
  const rows = [
    ...bookingRows(src.bookings || [], src.clubInfo, src.bookingConfig),
    ...activityRows(src.activities || [], src.players || [], src.clubInfo),
    ...seriespillRows(src.tournaments || [], src.clubInfo),
  ];
  rows.sort(
    (a, b) =>
      b.date.localeCompare(a.date) ||
      (b.time || '').localeCompare(a.time || '') ||
      a.title.localeCompare(b.title, 'nb'),
  );
  return rows;
}

/** Distinct centres in a ledger, the club's own centre first. */
export function centresOf(rows: ReportTransaction[], clubInfo: any): string[] {
  const home = homeCentreOf(clubInfo);
  const set = new Set<string>([home]);
  rows.forEach((r) => set.add(r.centre));
  return [...set].sort(
    (a, b) => Number(b === home) - Number(a === home) || a.localeCompare(b, 'nb'),
  );
}

/** Distinct `YYYY-MM` keys present in the ledger, newest first. */
export function monthsOf(rows: ReportTransaction[]): string[] {
  const set = new Set<string>();
  rows.forEach((r) => {
    const k = monthKeyOf(r.date);
    if (k) set.add(k);
  });
  return [...set].sort((a, b) => b.localeCompare(a));
}

export interface LedgerFilter {
  month: string;
  centre?: string | null;
  includeDemo?: boolean;
}

/** Rows for one month (+ centre), demo rows excluded unless asked for. */
export function filterLedger(rows: ReportTransaction[], f: LedgerFilter): ReportTransaction[] {
  return rows.filter(
    (r) =>
      monthKeyOf(r.date) === f.month &&
      (!f.centre || r.centre === f.centre) &&
      (f.includeDemo || !r.demo),
  );
}

// ─── settlement ───

export interface KindTotal {
  count: number;
  grossNok: number;
}

export interface Settlement {
  /** Sum of PAID rows. */
  grossNok: number;
  paidCount: number;
  /** Rows still owed (status pending). */
  pendingNok: number;
  pendingCount: number;
  /** Refunded rows — shown for transparency, not part of gross. */
  refundedNok: number;
  refundedCount: number;
  freeCount: number;
  /** Percentage fee over the paid rows. */
  pctFeeNok: number;
  /** Fixed monthly fee (0 when the month has no rows at all). */
  fixedFeeNok: number;
  totalFeeNok: number;
  /** Gross − fees. */
  netNok: number;
  byKind: Record<ReportTxKind, KindTotal>;
  byMethod: Record<ReportTxMethod, KindTotal>;
  /** Effective platform take as a share of gross (0 when gross is 0). */
  effectivePct: number;
}

/** Fee on a single paid row. */
export function rowFee(row: ReportTransaction, fees: ReportingFees): number {
  if (row.status !== 'paid') return 0;
  return roundOre((row.amountNok * (Number(fees.feePct) || 0)) / 100);
}

const emptyTotal = (): KindTotal => ({ count: 0, grossNok: 0 });

/** Settle one month of rows against the platform fee model. */
export function settle(rows: ReportTransaction[], fees: ReportingFees): Settlement {
  const s: Settlement = {
    grossNok: 0,
    paidCount: 0,
    pendingNok: 0,
    pendingCount: 0,
    refundedNok: 0,
    refundedCount: 0,
    freeCount: 0,
    pctFeeNok: 0,
    fixedFeeNok: 0,
    totalFeeNok: 0,
    netNok: 0,
    byKind: { booking: emptyTotal(), activity: emptyTotal(), seriespill: emptyTotal() },
    byMethod: {
      vipps: emptyTotal(),
      card: emptyTotal(),
      manual: emptyTotal(),
      booker_cover: emptyTotal(),
      none: emptyTotal(),
    },
    effectivePct: 0,
  };
  rows.forEach((r) => {
    if (r.status === 'paid') {
      s.grossNok += r.amountNok;
      s.paidCount += 1;
      s.pctFeeNok += rowFee(r, fees);
      s.byKind[r.kind].count += 1;
      s.byKind[r.kind].grossNok += r.amountNok;
      s.byMethod[r.method].count += 1;
      s.byMethod[r.method].grossNok += r.amountNok;
    } else if (r.status === 'pending') {
      s.pendingNok += r.amountNok;
      s.pendingCount += 1;
    } else if (r.status === 'refunded') {
      s.refundedNok += r.amountNok;
      s.refundedCount += 1;
    } else {
      s.freeCount += 1;
    }
  });
  s.grossNok = roundOre(s.grossNok);
  s.pctFeeNok = roundOre(s.pctFeeNok);
  s.fixedFeeNok = rows.length ? Math.max(0, Number(fees.monthlyFeeNok) || 0) : 0;
  s.totalFeeNok = roundOre(s.pctFeeNok + s.fixedFeeNok);
  s.netNok = roundOre(s.grossNok - s.totalFeeNok);
  s.effectivePct = s.grossNok > 0 ? roundOre((s.totalFeeNok / s.grossNok) * 100) : 0;
  return s;
}

// ─── export ───

export const TX_KIND_LABEL: Record<ReportTxKind, string> = {
  booking: 'Banebooking',
  activity: 'Aktivitet',
  seriespill: 'Seriespill',
};

export const TX_STATUS_LABEL: Record<ReportTxStatus, string> = {
  paid: 'Betalt',
  pending: 'Utestående',
  free: 'Gratis',
  refunded: 'Refundert',
};

export const TX_METHOD_LABEL: Record<ReportTxMethod, string> = {
  vipps: 'Vipps',
  card: 'Kort',
  manual: 'Manuelt',
  booker_cover: 'Dekket av booker',
  none: '—',
};

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v);
  return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const csvNumber = (n: number) => String(roundOre(n)).replace('.', ',');

/**
 * Semicolon-separated CSV (Norwegian Excel opens it directly), UTF-8 BOM, one
 * line per transaction plus the settlement summary at the end.
 */
export function ledgerToCsv(
  rows: ReportTransaction[],
  fees: ReportingFees,
  meta: { centre: string; month: string },
): string {
  const head = [
    'Dato',
    'Tid',
    'Type',
    'Beskrivelse',
    'Detalj',
    'Betaler',
    'Status',
    'Betalingsmåte',
    'Referanse',
    'Beløp (NOK)',
    'Plattformavgift (NOK)',
    'Netto (NOK)',
  ];
  const lines = rows.map((r) => {
    const fee = rowFee(r, fees);
    const counted = r.status === 'paid' ? r.amountNok : 0;
    return [
      r.date,
      r.time || '',
      TX_KIND_LABEL[r.kind],
      r.title,
      r.detail,
      r.payer,
      TX_STATUS_LABEL[r.status],
      TX_METHOD_LABEL[r.method],
      r.reference || '',
      csvNumber(r.amountNok),
      csvNumber(fee),
      csvNumber(counted - fee),
    ]
      .map(csvCell)
      .join(';');
  });
  const s = settle(rows, fees);
  const summary = [
    '',
    `Senter;${csvCell(meta.centre)}`,
    `Periode;${csvCell(monthLabel(meta.month))}`,
    `Brutto omsetning (betalt);${csvNumber(s.grossNok)}`,
    `Transaksjonsavgift ${csvNumber(Number(fees.feePct) || 0)} %;${csvNumber(s.pctFeeNok)}`,
    `Fast månedsavgift;${csvNumber(s.fixedFeeNok)}`,
    `Til utbetaling;${csvNumber(s.netNok)}`,
    `Utestående;${csvNumber(s.pendingNok)}`,
    `Refundert;${csvNumber(s.refundedNok)}`,
  ];
  return '\uFEFF' + [head.join(';'), ...lines, ...summary].join('\r\n');
}
